import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Draggable from 'react-draggable';
import { CheckCircle, KeyboardDoubleArrowDown, CloudUpload, AutoFixHigh, ErrorOutline, InfoOutlined, DragIndicator } from '@mui/icons-material';
import socket, { getRequest, postRequest, showMessage } from '../../config';
import { toggleModal } from '../../reducers/modalReducer';
import { updateFileAllocation } from '../../reducers/filesAllocationReducer';


const barBg = { background: '#E5E7EB', borderRadius: '10px', height: '8px', overflow: 'hidden', marginBottom: '0.75rem' };
const barFill = (w) => ({ background: 'linear-gradient(90deg, #4a67ff 0%, #3d56d9 100%)', height: '100%', borderRadius: '10px', width: `${w}%`, transition: 'width 0.3s ease' });
const statBox = (bg) => ({ background: bg, borderRadius: '8px', padding: '0.5rem', textAlign: 'center' });
const ellipsis = { margin: 0, textAlign: 'center', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };

const ProgressBar = ({ percent }) => (<div style={barBg}><div style={barFill(percent)} /></div>);

const AutoFillStatusBar = () => {
    const [isExpanded, setIsExpanded] = useState(true);
    const [fetchingReport, setFetchingReport] = useState(false);
    const nodeRef = useRef(null);
    const dispatch = useDispatch();
    const { fileReport } = useSelector(state => state.fileAllocation);
    const lastSocketEvent = useRef(0);
    const cancelledRef = useRef(false);

    // Use local state for all status bar data to avoid triggering re-renders in Company/index.js
    const [status, setStatus] = useState({ visible: false, phase: '', moduleName: '', total: 0, current: 0, currentQuestion: '', questionText: '', sectionNo: '', sectionName: '', readingFiles: [], startTime: null, uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null, withFilling: false, crossFileModuleType: '', otherModuleType: '' });

    // Sync from Redux modal → local state (only for initial triggers from AutoFillButton/QuesAns)
    const modal = useSelector(state => state.modal);
    const prevPhaseRef = useRef('');
    useEffect(() => {
        if (modal.autoFillStatusBarVisible && !status.visible) {
            cancelledRef.current = false;
            setStatus(s => ({ ...s, visible: true, phase: modal.autoFillStatusBarPhase || s.phase, moduleName: modal.autoFillModuleName || s.moduleName, uploadProgress: modal.autoFillUploadProgress || s.uploadProgress, withFilling: !!modal.autoFillWithFilling }));
        }
        // When Redux explicitly clears the status bar (full reset), hide local state too
        if (modal.autoFillStatusBarVisible === false && !modal.autoFillStatusBarPhase && status.visible) {
            setStatus(s => ({ ...s, visible: false }));
        }
        // Sync phase changes initiated externally (e.g., uploading→indexing→allocated from AutoFillButton)
        if (modal.autoFillStatusBarPhase && modal.autoFillStatusBarPhase !== prevPhaseRef.current && modal.autoFillStatusBarPhase !== status.phase) {
            prevPhaseRef.current = modal.autoFillStatusBarPhase;
            // Map stepper phases to status bar phases
            const mappedPhase = modal.autoFillStatusBarPhase === 'crossFileUpload' ? 'crossFileUpload'
                : modal.autoFillStatusBarPhase === 'otherModules' ? 'otherModulesAI'
                : modal.autoFillStatusBarPhase;
            setStatus(s => ({ ...s, phase: mappedPhase, moduleName: modal.autoFillModuleName || s.moduleName, uploadProgress: modal.autoFillUploadProgress ?? s.uploadProgress, indexingProgress: modal.autoFillIndexingProgress ?? s.indexingProgress, total: 0, current: 0, questionText: '', readingFiles: [] }));
        }
    }, [modal.autoFillStatusBarVisible, modal.autoFillStatusBarPhase, modal.autoFillModuleName, modal.autoFillUploadProgress, modal.autoFillIndexingProgress]);

    // Fetch file report from server by ID (used on page refresh restore)
    const fetchFileReport = async (fileReportId) => {
        try {
            const res = await getRequest(`/company/getFileReport/${fileReportId}`);
            if (res?.code === 200 && res?.body) {
                dispatch(updateFileAllocation({ fileReport: res.body }));
                return res.body;
            }
        } catch (e) {
            console.error('Failed to fetch file report:', e);
        }
        return null;
    };

    // Restore state on page refresh via socket — backend re-emits 'autoFill:currentStatus'
    // automatically when the socket reconnects and re-registers (see sockets.js register handler)
    useEffect(() => {
        const handleCurrentStatus = (operation) => {
            if (!operation || status.visible) return;
            const activePhases = ['indexing', 'processingFiles', 'filling'];
            if (activePhases.includes(operation.phase)) {
                const withFilling = operation.withFilling !== undefined ? !!operation.withFilling : operation.source !== 'temp';
                setStatus({ visible: true, phase: operation.phase, moduleName: operation.moduleName || '', total: operation.total || 0, current: operation.current || 0, currentQuestion: operation.currentQuestion || '', uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: operation.indexingProgress || null, source: operation.source || '', withFilling });
                // Restore autoFillContext so confirm-allocation uses the correct route/moduleId after refresh
                if (operation.companyId && operation.moduleType) {
                    dispatch(updateFileAllocation({
                        autoFillContext: {
                            enabled: true,
                            companyId: operation.companyId,
                            moduleType: operation.moduleType,
                            moduleId: operation.moduleId || null,
                            sectionCode: operation.sectionCode || null,
                            scope: operation.scope || 'module',
                            scopeName: operation.scopeName || '',
                            extraModules: operation.extraModules || [],
                        },
                        tab: operation.moduleType,
                    }));
                }
                const restoreTime = Date.now();
                const staleTimer = setTimeout(() => {
                    if (lastSocketEvent.current < restoreTime) setStatus(s => ({ ...s, phase: 'stale' }));
                }, 15000);
                return () => clearTimeout(staleTimer);
            } else if (operation.phase === 'allocated') {
                // Always restore to status bar on refresh — user clicks maximize to open modal
                if (operation.companyId && operation.moduleType) {
                    dispatch(updateFileAllocation({
                        autoFillContext: {
                            enabled: true,
                            companyId: operation.companyId,
                            moduleType: operation.moduleType,
                            moduleId: operation.moduleId || null,
                            sectionCode: operation.sectionCode || null,
                            scope: operation.scope || 'module',
                            scopeName: operation.scopeName || '',
                            extraModules: operation.extraModules || [],
                        },
                        tab: operation.moduleType,
                    }));
                }
                // Pre-fetch report into Redux so maximize opens instantly without loading
                if (operation.fileReportId) {
                    fetchFileReport(operation.fileReportId).then((report) => {
                        if (report?.importResult) dispatch(updateFileAllocation({ fileReport: report, currentScore: report.currentScore || 0 }));
                    });
                }
                setStatus({ visible: true, phase: 'allocated', moduleName: operation.moduleName || '', total: 0, current: 0, currentQuestion: '', uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null, withFilling: operation.withFilling !== false });
            } else if (operation.phase === 'otherModules') {
                if (operation.companyId && operation.moduleType) {
                    dispatch(updateFileAllocation({
                        autoFillContext: {
                            enabled: true,
                            companyId: operation.companyId,
                            moduleType: operation.moduleType,
                            moduleId: operation.moduleId || null,
                            sectionCode: operation.sectionCode || null,
                            scope: operation.scope || 'module',
                            scopeName: operation.scopeName || '',
                            extraModules: operation.targetModules || [],
                        },
                        tab: operation.moduleType,
                    }));
                }
                dispatch(toggleModal({
                    open: 'autoFillStepper',
                    autoFillStepperPhase: 'otherModules',
                    autoFillStatusBarPhase: 'otherModules',
                    autoFillStatusBarVisible: false,
                    autoFillWithFilling: true,
                }));
            }
        };

        socket.on('autoFill:currentStatus', handleCurrentStatus);
        return () => {
            socket.off('autoFill:currentStatus', handleCurrentStatus);
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Socket events — update local state only, no Redux dispatches
    useEffect(() => {
        const handleProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                const updates = { total: data.total || 0, current: data.current || 0, currentQuestion: data.currentQuestion || '', questionText: data.questionText || '', sectionNo: data.sectionNo || '', sectionName: data.sectionName || '', readingFiles: [] };
                if (s.phase === 'allocated') return { ...s, ...updates, phase: 'filling', startTime: Date.now() };
                if (s.phase !== 'filling') return s;
                return { ...s, ...updates, startTime: s.startTime || Date.now() };
            });
        };

        const handleReading = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase !== 'filling') return s;
                return { ...s, readingFiles: data.files || [] };
            });
        };

        const handleCompleted = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => ({ ...s, phase: 'completed', processed: data.processed || 0, answered: data.answered || 0, needsReview: data.needsReview || 0, skipped: data.skipped || 0 }));
        };

        const handleIndexingProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase !== 'indexing') return s;
                return { ...s, indexingProgress: { total: data.total || 0, current: data.current || 0, currentFile: data.currentFile || '' } };
            });
        };

        const handleAllocationCompleted = (data) => {
            // Ignore if user cancelled — don't re-show the status bar
            if (cancelledRef.current) return;
            lastSocketEvent.current = Date.now();
            // Always transition indexing → allocated, not just on page refresh
            setStatus(s => {
                if (s.phase !== 'indexing') return s;
                return { ...s, phase: 'allocated' };
            });
            if (data?.fileReport) dispatch(updateFileAllocation({ fileReport: data.fileReport }));
        };

        const handleProcessingFilesStarted = (data) => {
            lastSocketEvent.current = Date.now();
            // Don't force visible — user must click minimize to show status bar.
            // Just update state in background so it's ready if they minimize.
            setStatus(s => ({ ...s, phase: 'processingFiles', total: data.total || 0, current: 0 }));
        };

        const handleProcessingFilesProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase !== 'processingFiles') return s;
                return { ...s, current: data.current || 0, total: data.total || 0 };
            });
        };

        const handleProcessingFilesCompleted = () => {
            lastSocketEvent.current = Date.now();
            // Don't transition to 'filling' here — runAutoFill in importSummary handles it
            // via dispatch(toggleModal({ autoFillStatusBarPhase: 'filling' })).
            // Transitioning early would show the filling UI before autofill HTTP call is made.
        };

        const handleCancelled = () => {
            cancelledRef.current = true;
            setStatus({ visible: false, phase: '', moduleName: '', total: 0, current: 0, currentQuestion: '', questionText: '', sectionNo: '', sectionName: '', readingFiles: [], startTime: null, uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null, crossFileModuleType: '', otherModuleType: '' });
            dispatch(toggleModal({ autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
        };

        // Cross File Upload step — otherModules:* events from fillOtherModules
        const handleCrossFileProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase === 'otherModulesAI') return s;
                return { ...s, phase: 'crossFileUpload', total: data.total || s.total, current: data.current || s.current, crossFileModuleType: data.moduleType || s.crossFileModuleType };
            });
        };
        const handleCrossFileModuleCompleted = () => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase === 'otherModulesAI') return s;
                return { ...s, crossFileModuleType: '' };
            });
        };

        // AI Other Modules step — autoFill:* events per module call
        const handleAiOtherProgress = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase !== 'otherModulesAI') return s;
                return { ...s, total: data.total || s.total, current: data.current || s.current, questionText: data.questionText || s.questionText, otherModuleType: data.moduleType || s.otherModuleType };
            });
        };
        const handleAiOtherReading = (data) => {
            lastSocketEvent.current = Date.now();
            setStatus(s => {
                if (s.phase !== 'otherModulesAI') return s;
                return { ...s, readingFiles: data.files || [] };
            });
        };

        socket.on('autoFill:progress', handleProgress);
        socket.on('tempAutoFill:progress', handleProgress);
        socket.on('autoFill:reading', handleReading);
        socket.on('tempAutoFill:reading', handleReading);
        socket.on('autoFill:completed', handleCompleted);
        socket.on('tempAutoFill:completed', handleCompleted);
        socket.on('autoFill:cancelled', handleCancelled);
        socket.on('tempAutoFill:cancelled', handleCancelled);
        socket.on('indexing:progress', handleIndexingProgress);
        socket.on('tempIndexing:progress', handleIndexingProgress);
        socket.on('allocation:completed', handleAllocationCompleted);
        socket.on('tempAllocation:completed', handleAllocationCompleted);
        socket.on('processingFiles:started', handleProcessingFilesStarted);
        socket.on('tempProcessingFiles:started', handleProcessingFilesStarted);
        socket.on('processingFiles:progress', handleProcessingFilesProgress);
        socket.on('tempProcessingFiles:progress', handleProcessingFilesProgress);
        socket.on('processingFiles:completed', handleProcessingFilesCompleted);
        socket.on('tempProcessingFiles:completed', handleProcessingFilesCompleted);
        socket.on('otherModules:progress', handleCrossFileProgress);
        socket.on('otherModules:moduleCompleted', handleCrossFileModuleCompleted);
        socket.on('autoFill:progress', handleAiOtherProgress);
        socket.on('tempAutoFill:progress', handleAiOtherProgress);
        socket.on('autoFill:reading', handleAiOtherReading);
        socket.on('tempAutoFill:reading', handleAiOtherReading);
        return () => {
            socket.off('autoFill:progress', handleProgress);
            socket.off('tempAutoFill:progress', handleProgress);
            socket.off('autoFill:reading', handleReading);
            socket.off('tempAutoFill:reading', handleReading);
            socket.off('autoFill:completed', handleCompleted);
            socket.off('tempAutoFill:completed', handleCompleted);
            socket.off('autoFill:cancelled', handleCancelled);
            socket.off('tempAutoFill:cancelled', handleCancelled);
            socket.off('indexing:progress', handleIndexingProgress);
            socket.off('tempIndexing:progress', handleIndexingProgress);
            socket.off('allocation:completed', handleAllocationCompleted);
            socket.off('tempAllocation:completed', handleAllocationCompleted);
            socket.off('processingFiles:started', handleProcessingFilesStarted);
            socket.off('tempProcessingFiles:started', handleProcessingFilesStarted);
            socket.off('processingFiles:progress', handleProcessingFilesProgress);
            socket.off('tempProcessingFiles:progress', handleProcessingFilesProgress);
            socket.off('processingFiles:completed', handleProcessingFilesCompleted);
            socket.off('tempProcessingFiles:completed', handleProcessingFilesCompleted);
            socket.off('otherModules:progress', handleCrossFileProgress);
            socket.off('otherModules:moduleCompleted', handleCrossFileModuleCompleted);
            socket.off('autoFill:progress', handleAiOtherProgress);
            socket.off('tempAutoFill:progress', handleAiOtherProgress);
            socket.off('autoFill:reading', handleAiOtherReading);
            socket.off('tempAutoFill:reading', handleAiOtherReading);
        };
    }, [dispatch]);

    // Don't render status bar if it's not visible, or if a full modal is already showing
    const modalOpen = modal?.open;
    if (!status.visible || modalOpen === 'autoFillingQuestions' || modalOpen === 'computerToClarence' || modalOpen === 'allocatingFiles' || modalOpen === 'importSummary' || modalOpen === 'autoFillStepper') return null;

    const handleClose = () => {
        setStatus({ visible: false, phase: '', moduleName: '', total: 0, current: 0, currentQuestion: '', questionText: '', sectionNo: '', sectionName: '', readingFiles: [], startTime: null, uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null });
        dispatch(toggleModal({ autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
    };

    const handleCancel = async () => {
        cancelledRef.current = true;
        try {
            await postRequest('/clarence/auto-fill/cancel', {});
        } catch (e) { /* server will still process the cancel flag */ }
        setStatus({ visible: false, phase: '', moduleName: '', total: 0, current: 0, currentQuestion: '', questionText: '', sectionNo: '', sectionName: '', readingFiles: [], startTime: null, uploadProgress: null, processed: 0, answered: 0, needsReview: 0, skipped: 0, error: '', indexingProgress: null });
        dispatch(toggleModal({ autoFillStatusBarVisible: false, autoFillStatusBarPhase: '' }));
        showMessage('Auto-fill cancelled');
    };

    const getHeaderText = () => {
        switch (status.phase) {
            case 'uploading': { const p = status.uploadProgress || {}; return `Uploading ${p.current || 0} of ${p.total || 0} files`; }
            case 'indexing': { const idx = status.indexingProgress || {}; return idx.total > 0 ? `Indexing ${idx.current || 0} of ${idx.total} files` : 'Indexing files...'; }
            case 'processingFiles': return status.total > 0 ? `Processing ${status.current || 0} of ${status.total} files` : 'Processing files...';
            case 'allocated': return 'Files allocated - tap to review';
            case 'filling': return status.total > 0 ? `Auto-filling ${status.current || 0} of ${status.total} questions` : 'Analyzing questions...';
            case 'crossFileUpload': return status.total > 0 ? `Uploading ${status.current || 0} of ${status.total} files to other modules` : 'Uploading files to other modules...';
            case 'otherModulesAI': return status.total > 0 ? `Filling answers ${status.current || 0} of ${status.total} questions` : 'Filling answers in other modules...';
            case 'completed': return 'Auto-Fill Complete!';
            case 'error': return 'Auto-Fill Failed';
            case 'stale': return 'Process may have completed';
            default: return 'Auto-Fill';
        }
    };

    const getPhaseIcon = () => {
        switch (status.phase) {
            case 'uploading': return <CloudUpload style={{ fontSize: '20px' }} />;
            case 'indexing': case 'processingFiles': case 'filling': case 'crossFileUpload': case 'otherModulesAI': return <AutoFixHigh style={{ fontSize: '20px' }} className="auto-fill-status-spin" />;
            case 'allocated': return <CheckCircle style={{ fontSize: '20px', color: '#4a67ff' }} />;
            case 'completed': return <CheckCircle style={{ fontSize: '20px', color: '#059669' }} />;
            case 'error': return <ErrorOutline style={{ fontSize: '20px', color: '#DC2626' }} />;
            case 'stale': return <InfoOutlined style={{ fontSize: '20px', color: '#D97706' }} />;
            default: return null;
        }
    };

    const getProgressPercent = () => {
        if (status.phase === 'uploading') { const p = status.uploadProgress || {}; return p.total > 0 ? Math.round((p.current / p.total) * 100) : 0; }
        if (status.phase === 'processingFiles') return status.total > 0 ? Math.round((status.current / status.total) * 100) : 0;
        if (status.phase === 'filling') return status.total > 0 ? Math.round((status.current / status.total) * 100) : 0;
        if (status.phase === 'crossFileUpload') return status.total > 0 ? Math.round((status.current / status.total) * 100) : 0;
        if (status.phase === 'otherModulesAI') return status.total > 0 ? Math.round((status.current / status.total) * 100) : 0;
        if (status.phase === 'completed') return 100;
        return 0;
    };

    const canClose = ['completed', 'error', 'stale'].includes(status.phase);
    const canCancel = ['uploading', 'indexing', 'processingFiles', 'filling', 'crossFileUpload', 'otherModulesAI'].includes(status.phase);

    const handleExpand = async () => {
        if (status.phase !== 'allocated') return;
        if (!fileReport?.importResult) {
            const reportId = fileReport?._id || fileReport?.id;
            if (reportId && !fetchingReport) {
                setFetchingReport(true);
                const report = await fetchFileReport(reportId);
                setFetchingReport(false);
                if (report?.importResult) {
                    setStatus(s => ({ ...s, visible: false }));
                    dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'allocation', from: '', autoFillStatusBarVisible: false, autoFillWithFilling: status.withFilling }));
                    return;
                }
            }
            showMessage('Allocation data is not available. Please try again.');
            return;
        }
        setStatus(s => ({ ...s, visible: false }));
        dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'allocation', from: '', autoFillStatusBarVisible: false, autoFillWithFilling: status.withFilling }));
    };

    const canMaximize = ['uploading', 'indexing', 'processingFiles', 'allocated', 'filling', 'crossFileUpload', 'otherModulesAI', 'completed'].includes(status.phase);

    const handleMaximize = () => {
        switch (status.phase) {
            case 'uploading':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({
                    open: 'autoFillStepper',
                    autoFillStepperPhase: 'uploading',
                    autoFillStatusBarVisible: false,
                    autoFillWithFilling: status.withFilling,
                    percent: status.uploadProgress?.percent || 0,
                    fileName: status.uploadProgress?.fileName || '',
                    totalFiles: status.uploadProgress?.total || 0,
                    filesUploaded: status.uploadProgress?.current || 0,
                }));
                break;
            case 'indexing':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'indexing', autoFillStatusBarVisible: false, autoFillWithFilling: status.withFilling }));
                break;
            case 'processingFiles':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'processing', autoFillStatusBarVisible: false, autoFillWithFilling: status.withFilling }));
                break;
            case 'allocated':
                handleExpand();
                break;
            case 'filling':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({
                    open: 'autoFillStepper',
                    autoFillStepperPhase: 'filling',
                    autoFillStatusBarVisible: false,
                    autoFillWithFilling: status.withFilling,
                    autoFillCompleted: false,
                    autoFillTotal: status.total,
                    autoFillCurrent: status.current,
                    autoFillCurrentQuestion: status.currentQuestion,
                    autoFillQuestionText: status.questionText,
                    autoFillSectionNo: status.sectionNo,
                    autoFillSectionName: status.sectionName,
                    autoFillReadingFiles: status.readingFiles,
                    autoFillStartTime: status.startTime,
                    autoFillModuleName: status.moduleName,
                }));
                break;
            case 'crossFileUpload':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'crossFileUpload', autoFillStatusBarVisible: false, autoFillWithFilling: status.withFilling }));
                break;
            case 'otherModulesAI':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({ open: 'autoFillStepper', autoFillStepperPhase: 'otherModules', autoFillStatusBarVisible: false, autoFillWithFilling: status.withFilling }));
                break;
            case 'completed':
                setStatus(s => ({ ...s, visible: false }));
                dispatch(toggleModal({
                    open: 'autoFillStepper',
                    autoFillStepperPhase: 'result',
                    autoFillStatusBarVisible: false,
                    autoFillWithFilling: status.withFilling,
                    autoFillCompleted: true,
                    autoFillProcessed: status.processed,
                    autoFillAnswered: status.answered,
                    autoFillNeedsReview: status.needsReview,
                    autoFillSkipped: status.skipped,
                    autoFillModuleName: status.moduleName,
                }));
                break;
            default: break;
        }
    };

    return (
        <Draggable nodeRef={nodeRef} handle=".auto-fill-drag-handle" bounds="parent">
            <div ref={nodeRef} className='pf bg1 shadow' style={{ zIndex: '10000', right: '10px', bottom: '80px', borderRadius: '16px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', width: '380px' }}>
                {/* Header */}
                <div className="df aic jcsb w100 pl1rem auto-fill-drag-handle" style={{ height: '60px', borderBottom: isExpanded ? '1px solid #eaeaea' : 'none', cursor: 'grab' }}>
                    <div className="df aic" style={{ gap: '8px', flex: 1, overflow: 'hidden' }}>
                        <DragIndicator style={{ fontSize: '18px', color: '#9CA3AF', flexShrink: 0 }} />
                        {getPhaseIcon()}
                        <h3 className="c15 fs14px" style={{ margin: 0, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{getHeaderText()}</h3>
                    </div>
                    <div className="df aic jcfe">
                        {canMaximize && (
                            <div className="cp px0-5rem c15 df aic" onClick={handleMaximize} title="Restore to full modal">
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M21 11V3H13L16.29 6.29L6.29 16.29L3 13V21H11L7.71 17.71L17.71 7.71L21 11Z" fill="#374151" />
                                </svg>
                            </div>
                        )}
                        <div className="cp px0-5rem c15 df aic" onClick={() => setIsExpanded(!isExpanded)} style={{ transition: 'transform 0.3s ease', transform: isExpanded ? 'rotate(0deg)' : 'rotate(180deg)' }}>
                            <KeyboardDoubleArrowDown />
                        </div>
                    </div>
                </div>

                {/* Content */}
                <div className="w100" style={{ maxHeight: isExpanded ? '300px' : '0px', overflowY: 'auto', transition: 'max-height 0.3s ease, opacity 0.3s ease', opacity: isExpanded ? 1 : 0 }}>
                    <div style={{ padding: '1rem' }}>
                        {status.moduleName && <p className="c4 fw600 fs12px" style={{ margin: '0 0 0.75rem', textAlign: 'center' }}>{status.moduleName}</p>}

                        {/* Uploading */}
                        {status.phase === 'uploading' && (<>
                            <ProgressBar percent={getProgressPercent()} />
                            {status.uploadProgress?.fileName && <p className="c27 fs12px" style={ellipsis}>{status.uploadProgress.fileName}</p>}
                        </>)}

                        {/* Indexing */}
                        {status.phase === 'indexing' && (() => {
                            const idx = status.indexingProgress || {};
                            const pct = idx.total > 0 ? Math.round((idx.current / idx.total) * 100) : 0;
                            return idx.total > 0 ? (<div>
                                <ProgressBar percent={pct} />
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{pct}% complete</p>
                                {idx.currentFile && <p className="c27 fs12px" style={ellipsis}>{idx.currentFile}</p>}
                            </div>) : (<div style={{ textAlign: 'center' }}>
                                <progress color="green" style={{ border: 'none', width: '90%' }} />
                                <p className="c27 fs12px" style={{ margin: '0.5rem 0 0' }}>Processing and indexing your files...</p>
                            </div>);
                        })()}

                        {/* Processing Files */}
                        {status.phase === 'processingFiles' && (() => {
                            const pct = getProgressPercent();
                            return status.total > 0 ? (<div>
                                <ProgressBar percent={pct} />
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{pct}% complete</p>
                                <p className="c27 fs12px" style={{ margin: 0, textAlign: 'center' }}>Updating and indexing files...</p>
                            </div>) : (<div style={{ textAlign: 'center' }}>
                                <progress color="green" style={{ border: 'none', width: '90%' }} />
                                <p className="c27 fs12px" style={{ margin: '0.5rem 0 0' }}>Updating and indexing files...</p>
                            </div>);
                        })()}

                        {/* Allocated */}
                        {status.phase === 'allocated' && (
                            <div style={{ textAlign: 'center' }}>
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.75rem' }}>Files have been allocated to sections.</p>
                                <button onClick={handleExpand} disabled={fetchingReport} className="bg3 bg4-hover c1 br10px px2rem py0-5rem cp fw500 fs12px">{fetchingReport ? 'Loading...' : 'Review Allocation'}</button>
                            </div>
                        )}

                        {/* Filling */}
                        {status.phase === 'filling' && (() => {
                            const eta = status.startTime && status.current > 0 && status.total > 0
                                ? (() => { const remaining = ((Date.now() - status.startTime) / status.current) * (status.total - status.current); return remaining > 60000 ? `~${Math.ceil(remaining / 60000)} min remaining` : `~${Math.ceil(remaining / 1000)} sec remaining`; })()
                                : null;
                            return <>
                                <ProgressBar percent={getProgressPercent()} />
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{status.total > 0 ? `${getProgressPercent()}% complete` : 'Analyzing questions...'}</p>
                                {status.sectionName && <p className="c27 fs11px" style={ellipsis}>{status.sectionNo ? `${status.sectionNo} - ${status.sectionName}` : status.sectionName}</p>}
                                {status.questionText && <p className="c15 fs11px" style={{ ...ellipsis, margin: '0.25rem 0' }}>{status.questionText}</p>}
                                {status.readingFiles?.length > 0 && <p className="c27 fs11px" style={ellipsis}>Reading: {status.readingFiles[0]}</p>}
                                {eta && <p className="c27 fs11px" style={{ margin: '0.25rem 0 0', textAlign: 'center' }}>{eta}</p>}
                            </>;
                        })()}

                        {/* Completed */}
                        {status.phase === 'completed' && (
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem' }}>
                                <div style={statBox('#F3F4F6')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1F2937' }}>{status.processed || 0}</div><div style={{ fontSize: '0.65rem', color: '#6B7280' }}>Processed</div></div>
                                <div style={statBox('#D1FAE5')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#059669' }}>{status.answered || 0}</div><div style={{ fontSize: '0.65rem', color: '#059669' }}>Answered</div></div>
                                <div style={statBox('#FEF3C7')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#D97706' }}>{status.needsReview || 0}</div><div style={{ fontSize: '0.65rem', color: '#D97706' }}>Needs Review</div></div>
                                <div style={statBox('#F3F4F6')}><div style={{ fontSize: '1.25rem', fontWeight: '700', color: '#6B7280' }}>{status.skipped || 0}</div><div style={{ fontSize: '0.65rem', color: '#6B7280' }}>Skipped</div></div>
                            </div>
                        )}

                        {/* Cross File Upload */}
                        {status.phase === 'crossFileUpload' && (() => {
                            const pct = getProgressPercent();
                            return <div>
                                <ProgressBar percent={pct} />
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{status.total > 0 ? `${pct}% complete` : 'Uploading files to modules...'}</p>
                                {status.crossFileModuleType && <p className="c27 fs11px" style={ellipsis}>Module: {status.crossFileModuleType}</p>}
                            </div>;
                        })()}

                        {/* AI Other Modules */}
                        {status.phase === 'otherModulesAI' && (() => {
                            const pct = getProgressPercent();
                            return <div>
                                <ProgressBar percent={pct} />
                                <p className="c15 fw500 fs12px" style={{ margin: '0 0 0.25rem', textAlign: 'center' }}>{status.total > 0 ? `${pct}% complete` : 'AI filling questions...'}</p>
                                {status.otherModuleType && <p className="c27 fs11px" style={ellipsis}>Module: {status.otherModuleType}</p>}
                                {status.questionText && <p className="c15 fs11px" style={{ ...ellipsis, margin: '0.25rem 0' }}>{status.questionText}</p>}
                                {status.readingFiles?.length > 0 && <p className="c27 fs11px" style={ellipsis}>Reading: {status.readingFiles[0]}</p>}
                            </div>;
                        })()}

                        {/* Error */}
                        {status.phase === 'error' && <p style={{ color: '#DC2626', fontSize: '0.85rem', textAlign: 'center', margin: 0 }}>{status.error || 'Something went wrong. Please try again.'}</p>}

                        {/* Stale */}
                        {status.phase === 'stale' && <p className="c27 fs12px" style={{ textAlign: 'center', margin: 0 }}>The page was refreshed. Please check your module for updates.</p>}

                        {/* Action buttons */}
                        <div className="df aic jcc" style={{ gap: '1rem', marginTop: '0.75rem' }}>
                            {canCancel && <button onClick={handleCancel} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#DC2626', fontSize: '12px', textDecoration: 'underline', padding: 0 }}>Cancel</button>}
                            {canClose && <button onClick={handleClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280', fontSize: '12px', textDecoration: 'underline', padding: 0 }}>Close</button>}
                        </div>
                    </div>
                </div>
            </div>
        </Draggable>
    );
};

export default AutoFillStatusBar;
